import type { IStore } from './../../stores/Store/index';
import {
  ApolloClient,
  from,
  type ApolloLink,
  createHttpLink,
  type HttpOptions,
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { type ErrorResponse, onError } from '@apollo/client/link/error';
import { v4 as uuidv4 } from 'uuid';

import fetch from 'cross-fetch';
import authServiceFactory from 'src/services/authServiceFactory';
import logInfo from '../../utils/logInfo';
import { logError } from 'src/utils/logError';
import { getAdvertiserIds } from 'src/utils/advertiserIds';

const X_REQUEST_ID = 'x-request-id';
const X_SEEK_ADVERTISER_ID = 'x-seek-advertiser-id';
const X_SEEK_WIDE_ADVERTISER_ID = 'x-seek-wide-advertiser-id';
const AUTHORIZATION = 'authorization';
const FEATURES = 'features';

const SEEK_REQUEST_COUNTRY_HEADER = 'seek-request-country';
const X_APP_VER = 'x-app-ver';
const X_APP_BRANCH = 'x-app-branch';
const SEEK_REQUEST_SITE_HEADER = 'seek-request-site';

import configurationService from 'src/services/configurationService';

export type GraphqlErrorHandler = (error: ErrorResponse) => void;

const createAuthLink = (getStore: () => IStore) =>
  setContext(async (_, { headers }) => {
    const authService = authServiceFactory();
    const token = await authService.getToken();
    if (!token) {
      logError({
        errorCode: 'NULL_TOKEN',
        description:
          'Unable to get token from authService for cm-graphql client',
      });
      return;
    }
    const { advertiserId: adcentreAdvertiserId, seekAdvertiserId } =
      getAdvertiserIds();

    const features = getStore().featureStore.graphClientFeatures ?? [];
    return {
      headers: {
        ...headers,
        [AUTHORIZATION]: `Bearer ${token}`,
        [X_SEEK_ADVERTISER_ID]: adcentreAdvertiserId,
        [X_SEEK_WIDE_ADVERTISER_ID]: seekAdvertiserId,
        ...(features.length > 0 ? { [FEATURES]: features.join(',') } : null),
      },
    };
  });

const createRequestIdLink = () =>
  setContext(async (_, { headers }) => ({
    headers: {
      ...headers,
      [X_REQUEST_ID]: uuidv4(),
    },
  }));

const createStandardSeekRequestHeaderLink = () => {
  const branch = configurationService.getAppBranch();
  const version = configurationService.getAppVersion();

  return setContext(async (_, { headers }) => ({
    headers: {
      ...headers,
      ...{
        // only include seek-request-country for now. we should consider looking at using @seek/hirer-graphql-client
        // Github link https://github.com/SEEK-Jobs/hirer-graphql-client
        [SEEK_REQUEST_COUNTRY_HEADER]: configurationService.getCountryCode(),
        [SEEK_REQUEST_SITE_HEADER]: 'cm-ui',
        ...(branch ? { [X_APP_BRANCH]: branch } : null),
        ...(version ? { [X_APP_VER]: version } : null),
      },
    },
  }));
};

const createErrorLink = (apiName: string, errorHandler?: GraphqlErrorHandler) =>
  onError((error) => {
    const { graphQLErrors, networkError, operation } = error;
    const xRequestId = operation.getContext().headers[X_REQUEST_ID];

    errorHandler?.(error);
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        logInfo(
          `[GraphQL error]: GraphqlName: ${apiName} X-Request-Id: ${xRequestId}, Message: ${message}, Location: ${JSON.stringify(
            locations,
          )}, Path: ${path}`,
        ),
      );
    }
    if (networkError) {
      logInfo(
        `[Network error]: GraphqlName: ${apiName} X-Request-Id: ${xRequestId}, Error: ${networkError}`,
      );
    }
  });

const createApiHttpLink = (
  uri: string,
  credentials: HttpOptions['credentials'],
) =>
  createHttpLink({
    uri,
    credentials,
    fetch: typeof window !== 'undefined' ? window.fetch : fetch,
  });

export interface GraphqlClientConfig {
  apiUri: string;
  apiName: string;
  onError?: GraphqlErrorHandler;
  getStore: () => IStore;
  cache?: InMemoryCache;
  credentials: HttpOptions['credentials'];
}

const createGraphqlClient = (config: GraphqlClientConfig) => {
  const {
    getStore,
    onError: onErrorHandler,
    apiName,
    apiUri,
    cache = new InMemoryCache(),
  } = config;
  const links: ApolloLink[] = [
    createAuthLink(getStore),
    createRequestIdLink(),
    createStandardSeekRequestHeaderLink(),
    createErrorLink(apiName, onErrorHandler),
    createApiHttpLink(apiUri, config.credentials),
  ];

  return new ApolloClient({
    cache,
    link: from(links),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  });
};

export default createGraphqlClient;
